/** Confidence pill — colored by score band, with the agent's own
 *  extraction notes / rationale exposed under a click-to-expand details
 *  element so the reviewer can see WHY the agent chose that number.
 *
 *  Used across every rich panel. The score band thresholds:
 *    >=80  green  (high confidence)
 *    >=50  amber  (medium)
 *    <50   red    (low)
 *  null/undefined renders a neutral "—" pill.
 */
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

interface Props {
  /** 0-100 score. null/undefined renders neutral. */
  score: unknown;
  /** Free-text rationale from the agent (extraction_notes, finding, etc.). */
  rationale?: string | null;
  /** Optional label override; default "Confidence". */
  label?: string;
}

function band(score: number): { cls: string; tone: string } {
  if (score >= 80) {
    return {
      cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-950/30 dark:text-green-300 dark:border-green-800',
      tone: 'High',
    };
  }
  if (score >= 50) {
    return {
      cls: 'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
      tone: 'Medium',
    };
  }
  return {
    cls: 'bg-red-50 text-red-800 border-red-300 dark:bg-red-950/30 dark:text-red-300 dark:border-red-800',
    tone: 'Low',
  };
}

export function ConfidenceBadge({ score, rationale, label = 'Confidence' }: Props) {
  // Only a finite number is a real score; NaN/Infinity/non-number → neutral.
  const raw = typeof score === 'number' && Number.isFinite(score) ? score : null;
  if (raw === null) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        {label}: —
      </Badge>
    );
  }
  // Clamp into the 0-100 band the pill claims — a stray out-of-range agent
  // value (e.g. 130 or -5) never renders literally past the band boundary.
  const n = Math.max(0, Math.min(100, Math.round(raw)));
  const b = band(n);
  const note = (rationale ?? '').trim();
  return (
    <span className="inline-flex items-center gap-1">
      <Badge variant="outline" className={`text-[10px] ${b.cls}`}>
        {label}: {n}/100 · {b.tone}
      </Badge>
      {note && (
        <details className="inline-block">
          <summary
            className="cursor-pointer list-none inline-flex"
            title="Why this rating"
            aria-label="Show confidence rationale"
          >
            <Info className="h-3 w-3 text-muted-foreground hover:text-foreground" aria-hidden="true" />
          </summary>
          <div className="mt-1 rounded border bg-muted/40 p-2 text-[11px] text-muted-foreground max-w-md whitespace-pre-wrap">
            {note}
          </div>
        </details>
      )}
    </span>
  );
}
